import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { DropdownSelect } from "@whoosh/design-system/src/components/controls";
import { CurrencyIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  Text,
  HStack,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import { setBookingRateOnBookingAtom } from "../../../../atoms/actions/setBookingRateOnBookingAtom/setBookingRateOnBookingAtom";
import { currentUserIsBookingOwnerAtom } from "../../../../atoms/selectors/currentUserIsBookingOwnerAtom/currentUserIsBookingOwnerAtom";
import { getBookingRateAtom } from "../../../../atoms/selectors/getBookingRateAtom/getBookingRateAtom";
import { isAlreadyPaidAtom } from "../../../../atoms/selectors/isAlreadyPaidAtom/isAlreadyPaidAtom";
import { BookingRatesControlInnerQuery } from "./__generated__/BookingRatesControlInnerQuery.graphql";
import { useGetDurationBookingRateOptions } from "./hooks/useGetDurationBookingRateOptions";

type Props = {
  queryRef: PreloadedQuery<BookingRatesControlInnerQuery>;
};

export const BookingRatesControlInner = ({ queryRef }: Props) => {
  const booking = usePreloadedQuery(
    BookingRatesControlInnerQueryString,
    queryRef
  );
  const currentRate = useAtomValue(getBookingRateAtom);
  const isCurrentRateLocked = currentRate?.isLocked ?? false;
  const setBookingRateOnBooking = useSetAtom(setBookingRateOnBookingAtom);

  const dropdownOptions = useGetDurationBookingRateOptions({
    bookingRef: booking.node ?? null,
  });

  const currentUserIsBookingOwner = useAtomValue(currentUserIsBookingOwnerAtom);
  const isAlreadyPaid = useAtomValue(isAlreadyPaidAtom);
  return dropdownOptions.length > 0 ? (
    <VStack padding={5} gap={5} backgroundColor="surface-primary" marginTop={2}>
      <HStack alignItems="center" gap={3}>
        <Box
          backgroundColor="surface-main-primary-subtlest"
          width={24}
          height={24}
          justifyContent="center"
          alignItems="center"
          style={{ borderRadius: 4 }}
        >
          <CurrencyIcon />
        </Box>
        <Text>{t`Booking Fee`}</Text>
      </HStack>
      <VStack flex={1}>
        <DropdownSelect
          isDisabled={!currentUserIsBookingOwner || isAlreadyPaid}
          hideOuterMargin
          isViewOnly={isCurrentRateLocked}
          testID="booking-rate-dropdown"
          hideChevron
          includeLeftElementInSelectedValue
          contentSizeOverride="regular"
          options={dropdownOptions}
          value={currentRate?.id}
          onChange={(rateId) => {
            setBookingRateOnBooking({ rate: { id: rateId } });
          }}
        />
      </VStack>
    </VStack>
  ) : null;
};

export const BookingRatesControlInnerQueryString = graphql`
  query BookingRatesControlInnerQuery(
    $bookingId: ID!
    $membershipTypeId: ID!
    $duration: Int
    $groupSize: Int
  ) {
    node(id: $bookingId) {
      ...useGetDurationBookingRateOptions_Booking
    }
  }
`;
